import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { createCheckoutSession, fetchTiers } from "@/lib/api";
import { Input } from "@/components/ui/input";

// Simple, plain-English feature lists
const FEATURES = {
  starter: [
    "3 property reports every month",
    "Ask for help any time you need it",
    "Beginner-friendly market guides",
    "Email support (same-day reply)",
  ],
  pro: [
    "Everything in Starter",
    "5 property reports every month",
    "2 'Deal of the Month' reports",
    "Alerts when good deals appear",
    "Step-by-step buying guidance",
    "Faster support (same day)",
  ],
  elite: [
    "Everything in Pro",
    "Your own senior advisor",
    "We hunt for off-market deals",
    "Custom plan built for you",
    "Direct phone line",
    "Priority help (same day)",
  ],
};

const ORDER = ["starter", "pro", "elite"];

function formatPrice(cents, currency) {
  const cur = (currency || "aud").toUpperCase();
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: cur,
    maximumFractionDigits: 0,
  });
}

function yearlySavingsPct(monthlyCents, yearlyCents) {
  const fullYear = monthlyCents * 12;
  if (!fullYear) return 0;
  return Math.round(((fullYear - yearlyCents) / fullYear) * 100);
}

export default function Pricing() {
  const [tiers, setTiers] = useState([]);
  const [email, setEmail] = useState("");
  const [cycle, setCycle] = useState("monthly"); // monthly | yearly
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTiers()
      .then((data) => {
        const map = Object.fromEntries(data.map((t) => [t.id, t]));
        setTiers(ORDER.map((id) => map[id]).filter(Boolean));
      })
      .catch(() => setError("Could not load plans. Please refresh."));
  }, []);

  const handleSubscribe = async (tierId) => {
    setLoading(`${tierId}-stripe`);
    try {
      const { url } = await createCheckoutSession({
        tier: tierId,
        cycle,
        email: email || undefined,
      });
      if (url) {
        window.location.href = url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      toast.error("Could not start checkout.", {
        description: e?.response?.data?.detail || e.message,
      });
      setLoading(null);
    }
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#0A0A0A] grain"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-16">
        {/* Heading */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="data-label mb-6">/ Pick your plan</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Three plans. <span className="italic text-white/60">Same honest help.</span>
            </h2>
            <p className="mt-5 text-white/65 text-base sm:text-lg max-w-xl">
              Start small, upgrade any time. Cancel whenever you want — no fees, no hassle.
            </p>
          </div>
          <div className="lg:col-span-5">
            <label className="data-label block mb-3">/ Optional · we'll prefill checkout</label>
            <Input
              data-testid="pricing-email-input"
              type="email"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 h-auto text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-gold"
            />
          </div>
        </div>

        {/* Cycle Toggle */}
        <div className="mb-12 flex items-center justify-center sm:justify-start gap-3">
          <div
            role="tablist"
            data-testid="cycle-toggle"
            className="inline-flex border border-white/15 p-1 bg-[#0F0F0F]"
          >
            <button
              role="tab"
              aria-selected={cycle === "monthly"}
              data-testid="cycle-monthly"
              onClick={() => setCycle("monthly")}
              className={`px-6 py-2.5 text-[11px] tracking-[0.3em] uppercase transition-colors ${
                cycle === "monthly"
                  ? "bg-gold text-ink font-semibold"
                  : "text-white/70 hover:text-white"
              }`}
            >
              Monthly
            </button>
            <button
              role="tab"
              aria-selected={cycle === "yearly"}
              data-testid="cycle-yearly"
              onClick={() => setCycle("yearly")}
              className={`px-6 py-2.5 text-[11px] tracking-[0.3em] uppercase transition-colors flex items-center gap-2 ${
                cycle === "yearly"
                  ? "bg-gold text-ink font-semibold"
                  : "text-white/70 hover:text-white"
              }`}
            >
              Yearly
              <span
                className={`font-mono text-[9px] tracking-wider px-1.5 py-0.5 ${
                  cycle === "yearly"
                    ? "bg-ink text-gold"
                    : "bg-gold/20 text-gold border border-gold/40"
                }`}
              >
                SAVE
              </span>
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-8 text-sm text-red-400 font-mono" data-testid="pricing-error">
            {error}
          </div>
        )}

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10">
          {tiers.map((t, idx) => {
            const featured = t.id === "pro";
            const cents = cycle === "monthly" ? t.monthly_cents : t.yearly_cents;
            const intervalLabel = cycle === "monthly" ? "month" : "year";
            const savings = yearlySavingsPct(t.monthly_cents, t.yearly_cents);

            return (
              <div
                key={t.id}
                data-testid={`pricing-card-${t.id}`}
                className={`relative bg-[#0F0F0F] p-10 lg:p-12 flex flex-col ${
                  featured ? "lg:-my-6 lg:py-16 lg:shadow-[0_0_60px_rgba(240,196,107,0.18)]" : ""
                }`}
              >
                {featured && <div className="absolute top-0 left-0 right-0 h-px gold-shimmer" />}

                <div className="flex items-center justify-between mb-6">
                  <span className="data-label">{`Plan · 0${idx + 1}`}</span>
                  {featured && (
                    <span className="font-mono text-[10px] tracking-[0.3em] text-gold uppercase border border-gold/40 px-2 py-1">
                      Most Popular
                    </span>
                  )}
                </div>

                <h3 className="font-display text-4xl sm:text-5xl mb-3 leading-none">{t.name}</h3>
                <p className="text-white/55 text-sm leading-relaxed mb-8 min-h-[60px]">
                  {t.description}
                </p>

                <div className="flex items-baseline gap-2 mb-2">
                  <span
                    className="font-display text-6xl text-white leading-none"
                    data-testid={`price-${t.id}`}
                  >
                    {formatPrice(cents, t.currency)}
                  </span>
                  <span className="font-mono text-xs tracking-wider text-white/40">
                    / {intervalLabel}
                  </span>
                </div>
                <div className="font-mono text-[11px] tracking-[0.25em] text-white/40 uppercase mb-10 h-4">
                  {cycle === "yearly" && savings > 0 ? (
                    <span className="text-gold">{`You save ${savings}%`}</span>
                  ) : (
                    <span>Billed {intervalLabel}ly</span>
                  )}
                </div>

                <ul className="space-y-3 mb-10 flex-1">
                  {FEATURES[t.id].map((f) => (
                    <li key={f} className="flex items-start gap-3 text-[14px] text-white/80">
                      <Check size={16} className="text-gold mt-1 shrink-0" strokeWidth={2.2} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div>
                  <button
                    data-testid={`subscribe-stripe-${t.id}`}
                    onClick={() => handleSubscribe(t.id)}
                    disabled={loading !== null}
                    className={`w-full inline-flex items-center justify-center gap-2 py-4 text-[12px] tracking-[0.25em] uppercase font-semibold transition-colors ${
                      featured
                        ? "bg-gold text-ink hover:bg-gold-dark"
                        : "border border-white/30 text-white hover:bg-white hover:text-ink"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {loading === `${t.id}-stripe` ? (
                      <Loader2 className="animate-spin" size={14} />
                    ) : (
                      "Subscribe with Card"
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-10 font-mono text-[11px] tracking-[0.2em] uppercase text-white/45 text-center">
          Cancel any time. No long contracts. No hidden fees.
          <br />
          <span className="normal-case tracking-normal text-white/40 text-[12px] font-sans">
            By subscribing you agree to our{" "}
            <a href="/terms" data-testid="pricing-terms-link" className="text-gold/80 hover:text-gold underline underline-offset-2">
              Terms &amp; Conditions
            </a>
            .
          </span>
        </p>
      </div>
    </section>
  );
}
